import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getAuth, onAuthStateChanged } from 'firebase/auth';
import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  getDocs,
  query,
  serverTimestamp,
  setDoc,
  where,
} from 'firebase/firestore';
import { db } from '../firebase';
import { bingoPatterns, createEmptyMask, getPatternLabel } from '../data/bingoPatterns';
import { duplicateProgramSchedule, sanitizeProgramSchedule } from '../utils/programTemplateUtils';
import './ProgramTemplates.css';

const serializeCustomPattern = (mask) => mask.flat();
const deserializeCustomPattern = (flat) => {
  if (!Array.isArray(flat)) return createEmptyMask();
  if (flat.length === 5 && flat.every(Array.isArray)) return flat.map((row) => row.map(Boolean));
  if (flat.length !== 25) return createEmptyMask();
  return Array.from({ length: 5 }, (_, row) => flat.slice(row * 5, row * 5 + 5).map(Boolean));
};

function ProgramTemplates() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [selectedId, setSelectedId] = useState('');
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [games, setGames] = useState([]);
  const [gameName, setGameName] = useState('');
  const [pattern, setPattern] = useState('');
  const [prize, setPrize] = useState('');
  const [customPattern, setCustomPattern] = useState(createEmptyMask);
  const [editingIndex, setEditingIndex] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');
  const [isEditorOpen, setIsEditorOpen] = useState(false);
  const editorRef = useRef(null);
  const nameInputRef = useRef(null);

  const loadTemplates = useCallback(async (currentUser) => {
    const snapshot = await getDocs(query(
      collection(db, 'programTemplates'),
      where('createdByUid', '==', currentUser.uid)
    ));
    setTemplates(snapshot.docs.map((item) => ({ id: item.id, ...item.data() })));
  }, []);

  useEffect(() => onAuthStateChanged(getAuth(), async (currentUser) => {
    setUser(currentUser || null);
    if (currentUser) {
      try {
        await loadTemplates(currentUser);
      } catch (error) {
        setMessage(error?.code === 'permission-denied'
          ? 'Firestore access to Program Templates is not enabled yet.'
          : 'Unable to load saved programs.');
        // eslint-disable-next-line no-console
        console.error('Error loading program templates:', error);
      }
    } else {
      setTemplates([]);
    }
    setLoading(false);
  }), [loadTemplates]);

  const clearGameForm = () => {
    setGameName('');
    setPattern('');
    setPrize('');
    setCustomPattern(createEmptyMask());
    setEditingIndex(null);
  };

  const newProgram = () => {
    setSelectedId('');
    setName('');
    setDescription('');
    setGames([]);
    clearGameForm();
    setMessage('');
    setIsEditorOpen(true);
    window.requestAnimationFrame(() => {
      editorRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      nameInputRef.current?.focus();
    });
  };

  const editProgram = (template) => {
    setSelectedId(template.id);
    setName(template.name || '');
    setDescription(template.description || '');
    setGames(sanitizeProgramSchedule(template.schedule));
    clearGameForm();
    setIsEditorOpen(true);
    window.requestAnimationFrame(() => editorRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }));
  };

  const addOrUpdateGame = () => {
    if (!pattern || !prize) return;
    const index = editingIndex === null ? games.length : editingIndex;
    const nextGame = {
      id: games[index]?.id || `game-${Date.now()}-${index + 1}`,
      gameNumber: index + 1,
      type: 'game',
      name: gameName.trim() || `Game ${index + 1}`,
      pattern,
      prizeAmount: prize,
      customPattern: pattern === 'custom' ? serializeCustomPattern(customPattern) : null,
    };
    setGames(editingIndex === null
      ? [...games, nextGame]
      : games.map((game, gameIndex) => gameIndex === editingIndex ? nextGame : game));
    clearGameForm();
  };

  const beginGameEdit = (index) => {
    const game = games[index];
    setGameName(game.name || '');
    setPattern(game.pattern || '');
    setPrize(game.prizeAmount || '');
    setCustomPattern(deserializeCustomPattern(game.customPattern));
    setEditingIndex(index);
  };

  const moveGame = (index, direction) => {
    const target = index + direction;
    if (target < 0 || target >= games.length) return;
    const reordered = [...games];
    [reordered[index], reordered[target]] = [reordered[target], reordered[index]];
    setGames(reordered.map((game, gameIndex) => ({ ...game, gameNumber: gameIndex + 1 })));
  };

  const duplicateGame = (index) => {
    const copy = { ...games[index], id: `game-${Date.now()}-${index + 2}` };
    const next = [...games.slice(0, index + 1), copy, ...games.slice(index + 1)]
      .map((game, gameIndex) => ({ ...game, gameNumber: gameIndex + 1 }));
    setGames(next);
  };

  const saveProgram = async () => {
    if (!user?.email || !name.trim() || saving) return;
    setSaving(true);
    setMessage('');
    const payload = {
      name: name.trim(),
      description: description.trim(),
      schedule: sanitizeProgramSchedule(games),
      createdBy: user.email,
      createdByUid: user.uid,
      updatedAt: serverTimestamp(),
    };
    try {
      if (selectedId) {
        await setDoc(doc(db, 'programTemplates', selectedId), payload, { merge: true });
      } else {
        const created = await addDoc(collection(db, 'programTemplates'), {
          ...payload,
          createdAt: serverTimestamp(),
        });
        setSelectedId(created.id);
      }
      await loadTemplates(user);
      setMessage('Program saved.');
    } catch (error) {
      setMessage(error?.code === 'permission-denied'
        ? 'Firestore blocked this save. Add access for the programTemplates collection in your Firestore rules.'
        : 'Unable to save this program. Please try again.');
      // eslint-disable-next-line no-console
      console.error('Error saving program template:', error);
    } finally { setSaving(false); }
  };

  const duplicateTemplate = async (template) => {
    if (!user?.email) return;
    await addDoc(collection(db, 'programTemplates'), {
      name: `${template.name || 'Untitled Program'} Copy`,
      description: template.description || '',
      schedule: duplicateProgramSchedule(template.schedule),
      createdBy: user.email,
      createdByUid: user.uid,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    });
    await loadTemplates(user);
  };

  const removeTemplate = async (template) => {
    if (!window.confirm(`Delete “${template.name}”? Events created from it will not be affected.`)) return;
    await deleteDoc(doc(db, 'programTemplates', template.id));
    if (selectedId === template.id) newProgram();
    await loadTemplates(user);
  };

  if (loading) return <main className="program-page"><p className="program-status">Loading saved programs…</p></main>;

  return (
    <main className="program-page">
      <div className="program-shell">
        <header className="program-header">
          <div><span className="machine-eyebrow">SAVED PROGRAMS</span><h1>Program Templates</h1><p>Build once, then reuse the complete game lineup for future events.</p></div>
          <div className="program-header-actions"><button onClick={newProgram}>Create Program</button><button onClick={() => navigate('/bingo-machine')}>Back</button></div>
        </header>
        {message && <p className="program-message">{message}</p>}

        {isEditorOpen && <section className="program-editor" ref={editorRef}>
          <div className="program-editor-meta">
            <input ref={nameInputRef} value={name} onChange={(event) => setName(event.target.value)} placeholder="Program name" />
            <textarea value={description} onChange={(event) => setDescription(event.target.value)} placeholder="Description (optional)" />
          </div>
          <div className="program-game-form">
            <input value={gameName} onChange={(event) => setGameName(event.target.value)} placeholder="Game name (optional)" />
            <select value={pattern} onChange={(event) => setPattern(event.target.value)}><option value="">Pattern</option>{bingoPatterns.map((item) => <option key={item.id} value={item.id}>{item.name}</option>)}</select>
            <input type="number" min="0" step="0.01" value={prize} onChange={(event) => setPrize(event.target.value)} placeholder="Prize" />
            <button onClick={addOrUpdateGame} disabled={!pattern || !prize}>{editingIndex === null ? '+ Add Game' : 'Save Game'}</button>
          </div>
          {pattern === 'custom' && <div className="program-custom-grid">{customPattern.map((row, r) => row.map((cell, c) => { const center = r === 2 && c === 2; return <button key={`${r}-${c}`} className={cell || center ? 'selected' : ''} disabled={center} onClick={() => setCustomPattern((current) => current.map((maskRow, ri) => ri === r ? maskRow.map((value, ci) => ci === c ? !value : value) : maskRow))}>{center ? 'FREE' : cell ? 'X' : ''}</button>; }))}</div>}
          <div className="program-games">{games.map((game, index) => <div className="program-game" key={game.id}><b>{index + 1}</b><span><strong>{game.name}</strong><small>{getPatternLabel(game.pattern)} · ${game.prizeAmount}</small></span><div><button onClick={() => moveGame(index, -1)} disabled={index === 0}>↑</button><button onClick={() => moveGame(index, 1)} disabled={index === games.length - 1}>↓</button><button onClick={() => beginGameEdit(index)}>Edit</button><button onClick={() => duplicateGame(index)}>Duplicate</button><button onClick={() => setGames(games.filter((_, i) => i !== index).map((item, i) => ({ ...item, gameNumber: i + 1 })))}>Remove</button></div></div>)}</div>
          <div className="program-save-actions"><button className="program-save" onClick={saveProgram} disabled={!name.trim() || saving}>{saving ? 'Saving…' : selectedId ? 'Save Changes' : 'Save Program'}</button><button onClick={() => setIsEditorOpen(false)}>Cancel</button></div>
        </section>}

        <section className="program-library"><h2>Your Saved Programs</h2>{templates.length === 0 ? <div className="program-empty"><strong>No saved programs yet.</strong><p>Build your bingo program once and reuse it for future events.</p><button onClick={newProgram}>Create Program</button></div> : <div className="program-cards">{templates.map((template) => <article key={template.id}><h3>{template.name}</h3>{template.description && <p>{template.description}</p>}<strong>{sanitizeProgramSchedule(template.schedule).length} games</strong>{template.updatedAt?.toDate && <small>Updated {template.updatedAt.toDate().toLocaleDateString()}</small>}<div><button className="primary" onClick={() => navigate(`/bingo-machine?templateId=${encodeURIComponent(template.id)}`)}>Use Template</button><button onClick={() => editProgram(template)}>Edit</button><button onClick={() => duplicateTemplate(template)}>Duplicate</button><button onClick={() => removeTemplate(template)}>Delete</button></div></article>)}</div>}</section>
      </div>
    </main>
  );
}

export default ProgramTemplates;
